/**
 * The gate in front of the invitation list: Firebase proves the address
 * before `GET /invitations` is worth asking (the endpoint itself answers an
 * unverified caller with an empty list rather than a refusal, so the client
 * is the only place that can tell the two apart -- see `useMyInvitations`).
 *
 * Verification is checked automatically, on mount and whenever the tab
 * regains focus, rather than behind a button the deck names no copy for: the
 * common case is a reader who clicked the email's link in another tab and
 * came back to this one, and `refreshVerification` (`reload` then a forced
 * token refresh) is what makes that visible here. `AuthProvider`'s
 * `onIdTokenChanged` listener re-renders the caller once the token refresh
 * lands, which is what moves this screen on -- nothing here polls or
 * navigates itself.
 */
import { useEffect, useState, type ReactNode } from 'react';

import { EmptyState } from '../../../chrome/state';
import { Button } from '../../../ui/button';
import { Spinner } from '../../../ui/spinner';
import { useAuthSession } from '../../auth/AuthProvider';
import { tt } from '../i18n';

export function VerifyAddress(): ReactNode {
  const { session } = useAuthSession();
  const [resending, setResending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    function check(): void {
      setRefreshing(true);
      void session.refreshVerification().finally(() => {
        if (!cancelled) setRefreshing(false);
      });
    }

    check();
    window.addEventListener('focus', check);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', check);
    };
  }, [session]);

  async function resend(): Promise<void> {
    setResending(true);
    try {
      await session.sendEmailVerification();
    } finally {
      setResending(false);
    }
  }

  return (
    <EmptyState
      title={tt('accept.verify_email.title')}
      description={tt('accept.verify_email.description')}
      action={
        <div className="flex flex-col items-center gap-2">
          {refreshing && <Spinner className="size-4" />}
          <p className="text-body-sm text-muted-foreground">
            {tt('accept.verify_email.resend_text')}
          </p>
          <Button
            type="button"
            variant="outline"
            disabled={resending}
            onClick={() => void resend()}
          >
            {tt('accept.verify_email.resend_action')}
          </Button>
        </div>
      }
    />
  );
}
